/**
 * Service layer for the Log Reviewer / Approver Insights screens.
 *
 * Charts the main Log Review dashboard already exposes (Compliance Status,
 * By Team) are reused from `logReviewApi`; the endpoints unique to Insights
 * live here:
 *
 *   - `getRequestStatusCount`                   → Request Status donut (reviewer)
 *   - `getDivisionHeadReviewCount`              → Request Status donut (CISO)
 *   - `getLogApproveInsightsTLList/{p}/{n}`     → reviewer Details list
 *   - `getLogApproveInsightsDivisionHeadLists`  → approver Details list
 *   - `review/{teamLead|divisionHead}`          → acknowledge / approve / reject
 */

import { LOG_REVIEW_URL } from "../constants/index";
import { LOG_INSIGHT_CONFIG } from "../app/types/logReview";
import type {
  LogInsightRisk,
  LogInsightRole,
  LogInsightRow,
  LogReviewServerNode,
  LogReviewSessionDetail,
  PieChartDatum,
} from "../app/types/logReview";
import type { LogReviewChartFilters } from "./logReviewApi";
import {
  logReviewPost,
  toNumber,
  toWriteResult,
  type LogReviewWriteResponse,
  type LogReviewWriteResult,
} from "./logReviewClient";

// ─── Request Status (donut) ──────────────────────────────────────────────

interface RawRequestStatusResponse {
  totalCount: number;
  details: Array<{ status: string; count: number | string; percentage: number | string }>;
}

/**
 * The statuses the Request Status donut always shows, in reading order.
 *
 * The endpoint only returns the statuses that currently have rows, so a
 * selection with nothing rejected drops "Rejected" from the payload entirely
 * and the legend changes shape between filters — a reader cannot tell "no
 * rejections" from "rejections are not tracked here".
 *
 * The vocabulary is the role's own, exactly as the Status filter facet lists
 * it: a Log Reviewer acknowledges what is Pending Review, a CISO approves
 * what is Pending Approval. Both come from LOG_INSIGHT_CONFIG so the donut,
 * the facet and the decision buttons can never drift apart.
 */
const requestStatuses = (role: LogInsightRole): string[] => {
  const cfg = LOG_INSIGHT_CONFIG[role];
  return [cfg.pendingStatus, cfg.positiveStatus, cfg.negativeStatus];
};

/**
 * Fold a payload status onto the role's own label for that workflow stage.
 *
 * The endpoint answers in the shared workflow vocabulary rather than the
 * caller's, so the reviewer queue is served "Approved" for what it calls
 * "Acknowledged", and either queue can be served "Pending Review". Left alone
 * those arrive as extra slices, which is the "Approved" legend showing up on
 * the reviewer chart. Aliasing keeps the count and drops the duplicate label.
 *
 * "Pending" is tested FIRST: "Pending Approval" contains "approv" and would
 * otherwise fold into the positive stage. Returns `null` for anything outside
 * the three stages, which is charted under its own name instead of vanishing.
 */
const canonicalRequestStatus = (status: string, role: LogInsightRole): string | null => {
  const cfg = LOG_INSIGHT_CONFIG[role];
  const value = status.toLowerCase();
  if (value.includes("pending")) return cfg.pendingStatus;
  if (value.includes("reject")) return cfg.negativeStatus;
  if (value.includes("approv") || value.includes("acknowledg")) return cfg.positiveStatus;
  return null;
};

/**
 * Merge the payload onto {@link requestStatuses} so every status keeps its
 * legend row — and its colour — at zero, with each row's count folded onto
 * the role's label for its stage first.
 *
 * An empty payload stays empty: three zeroed legends would read as a chart
 * with data, where the chart's own empty state says there is none.
 */
const toRequestStatusSlices = (
  details: RawRequestStatusResponse["details"],
  role: LogInsightRole,
): PieChartDatum[] => {
  const counts = new Map<string, PieChartDatum>();
  details.forEach((d) => {
    const raw = (d.status ?? "").trim();
    if (!raw) return;
    const name = canonicalRequestStatus(raw, role) ?? raw;
    const key = name.toLowerCase();
    const existing = counts.get(key);
    if (existing) existing.value += toNumber(d.count);
    else counts.set(key, { name, value: toNumber(d.count) });
  });
  if (counts.size === 0) return [];

  const fixed = requestStatuses(role).map((status) => {
    const key = status.toLowerCase();
    const hit = counts.get(key);
    counts.delete(key);
    return { name: status, value: hit?.value ?? 0 };
  });
  return [...fixed, ...counts.values()];
};

/**
 * Role decides which endpoint counts the ring, the same way it decides which
 * endpoint lists the queue:
 *   - reviewer (Team Lead)      → `getRequestStatusCount`
 *   - approver (Division Head)  → `getDivisionHeadReviewCount`
 * A ternary rather than a record lookup, for the same reason as
 * {@link listEndpoint}: it is not an object-injection sink.
 */
const requestStatusEndpoint = (role: LogInsightRole): string =>
  role === "approver" ? "getDivisionHeadReviewCount" : "getRequestStatusCount";

export const fetchRequestStatusCount = async (
  role: LogInsightRole,
  filters: LogReviewChartFilters,
  signal?: AbortSignal,
): Promise<PieChartDatum[]> => {
  const raw = await logReviewPost<RawRequestStatusResponse>(
    `${LOG_REVIEW_URL.SIRAR}/${requestStatusEndpoint(role)}`,
    filters,
    signal,
  );
  return toRequestStatusSlices(raw.details ?? [], role);
};

// ─── Details list (server-side paginated) ────────────────────────────────

/**
 * Payload for the Insights Details list. Mirrors the top filter bar
 * (appCode / category / division / date window) merged with the table's
 * own search keyword, server-side sort and drawer facets.
 */
export interface LogInsightsListParams {
  keyword: string;
  sortByColumn: string;
  sortOrder: "asc" | "desc" | "";
  appCode: string[];
  category: string[];
  division: string[];
  teams: string[];
  risk: string[];
  status: string[];
  criticality: string[];
  startDate: string;
  endDate: string;
}

export interface LogInsightsListResult {
  rows: LogInsightRow[];
  totalCount: number;
  /** Division-Head (approver) endpoint flag — gates Approve / Reject. */
  canApprove: boolean;
}

/**
 * Role decides which Details endpoint is hit:
 *   - reviewer (Team Lead)      → `getLogApproveInsightsTLList`
 *   - approver (Division Head)  → `getLogApproveInsightsDivisionHeadLists`
 * A switch (not a record lookup) avoids an object-injection sink.
 */
const listEndpoint = (role: LogInsightRole): string =>
  role === "approver"
    ? "getLogApproveInsightsDivisionHeadLists"
    : "getLogApproveInsightsTLList";

interface RawLogInsight {
  id: number;
  complianceType: string | null;
  changeRequestId: string;
  application: string;
  category: string;
  team: string[] | null;
  plannedStartTime: string | null;
  plannedEndTime: string | null;
  actualStartTime: string | null;
  actualEndTime: string | null;
  reasonForNonCompliance: string[] | null;
  servers: number | null;
  status: string;
  users: string[] | null;
  division: string | null;
  risk: string | null;
  criticality: string | null;
  requestTime: string | null;
  requestedBy: string | null;
  attachmentCount: number | null;
  requestId: string | null;
}

interface RawLogInsightListResponse {
  data: RawLogInsight[];
  totalCount: number;
  canApprove?: boolean;
}

// Risk arrives as "High - Some service degradation possible" — keep the
// severity prefix only and map it onto the Insights risk vocabulary.
const parseInsightRisk = (raw: string | null): LogInsightRisk => {
  const s = (raw ?? "").toLowerCase();
  if (s.startsWith("critical")) return "Critical";
  if (s.startsWith("high")) return "High";
  if (s.startsWith("medium")) return "Medium";
  if (s.startsWith("low")) return "Low";
  if (s.startsWith("info")) return "Informational";
  return "Low";
};

const toLogInsightRow = (raw: RawLogInsight): LogInsightRow => {
  const teams = raw.team ?? [];
  const users = raw.users ?? [];
  const reasons = raw.reasonForNonCompliance ?? [];
  const serverCount = toNumber(raw.servers);
  // Date/time strings are passed through exactly as the backend formats
  // them (`DD/MM/YYYY, HH:mm`). The frontend does not reparse or reformat
  // date fields — doing so previously round-tripped through a Date and
  // re-rendered them in 12-hour AM/PM, which the backend never sent.
  const requestedOn = raw.requestTime ?? "";
  const plannedStart = raw.plannedStartTime ?? "";
  const plannedEnd = raw.plannedEndTime ?? "";
  const actualStart = raw.actualStartTime ?? "";
  const actualEnd = raw.actualEndTime ?? "";

  // The list endpoint returns a server COUNT, not per-node detail. Expand
  // it into `serverCount` node rows (cycling the team list) so the
  // Servers/Nodes count link and its popup stay consistent with the API.
  const serverNodes: LogReviewServerNode[] = Array.from(
    { length: serverCount },
    (_, i) => ({
      team: teams.length ? teams[i % teams.length] : "—",
      serverCis: "—",
    }),
  );

  const sessionDetails: LogReviewSessionDetail[] = [
    {
      lanUserId: users.join(", "),
      serverNode: `${serverCount}`,
      targetAccount: "—",
      plannedStart,
      plannedEnd,
      actualStart,
      actualEnd,
      status: reasons.length
        ? `${raw.complianceType ?? raw.status} (${reasons.join(", ")})`
        : raw.complianceType ?? raw.status,
    },
  ];

  return {
    id: `${raw.id}`,
    requestId: raw.requestId ?? "",
    changeRequestId: raw.changeRequestId,
    application: raw.application,
    division: raw.division ?? "",
    criticality: raw.criticality ?? "",
    risk: parseInsightRisk(raw.risk),
    team: teams,
    users,
    justifiedBy: raw.requestedBy ?? "",
    justifiedOn: requestedOn,
    plannedStart,
    plannedEnd,
    actualStart,
    actualEnd,
    servers: Array.from({ length: serverCount }, (_, i) => `${i + 1}`),
    status: raw.status,
    reasons,
    attachmentCount: toNumber(raw.attachmentCount),
    actionPlan: {
      actionPlan: "—",
      description: "—",
      targetDate: "",
      requestedBy: raw.requestedBy ?? "",
      requestedOn,
      comment: "—",
      commentedBy: "—",
      commentedOn: "—",
      status: raw.status,
    },
    serverNodes,
    sessionDetails,
  };
};

/**
 * Server-side Details list. Per the backend URL convention the path order
 * is `{pageNumber}/{itemsPerPage}` — pageNumber first. The endpoint is
 * selected by `role` (Team Lead vs Division Head).
 */
export const fetchLogInsightsList = async (
  role: LogInsightRole,
  params: LogInsightsListParams,
  pageNumber = 0,
  itemsPerPage = 10,
  signal?: AbortSignal,
): Promise<LogInsightsListResult> => {
  const raw = await logReviewPost<RawLogInsightListResponse>(
    `${LOG_REVIEW_URL.SIRAR}/${listEndpoint(role)}/${pageNumber}/${itemsPerPage}`,
    params,
    signal,
  );
  return {
    rows: (raw.data ?? []).map(toLogInsightRow),
    totalCount: raw.totalCount ?? 0,
    canApprove: raw.canApprove ?? true,
  };
};

// ─── Acknowledge / Approve / Reject (review decision) ────────────────────

export type LogInsightsReviewResult = LogReviewWriteResult;

/**
 * Submit a review decision for the selected CR(s). The endpoint is chosen
 * by role — reviewer (Team Lead) vs approver (Division Head) — and success
 * is determined by the response `statusCodeValue` (2xx ⇒ accepted), with
 * the `body` string surfaced as the user-facing message.
 */
export const submitLogInsightsReview = async (
  role: LogInsightRole,
  ids: number[],
  decision: "positive" | "negative",
  comment: string,
  commentBy: string,
  signal?: AbortSignal,
): Promise<LogInsightsReviewResult> => {
  const endpoint = role === "approver" ? "divisionHead" : "teamLead";
  const raw = await logReviewPost<LogReviewWriteResponse>(
    `${LOG_REVIEW_URL.SIRAR}/review/${endpoint}`,
    {
      id: ids,
      comment,
      approvedRejectedStatus: decision === "positive" ? "Approved" : "Rejected",
      commentBy,
    },
    signal,
  );
  return toWriteResult(raw ?? {});
};
